import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  Edit,
  FileText,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { cn } from "@/lib/utils";

const apInvoiceFormSchema = z.object({
  status: z.string().min(1, "Status is required"),
  dueDate: z.string().optional(),
  paymentTerms: z.string().optional(),
  notes: z.string().optional(),
});

type ApInvoiceFormData = z.infer<typeof apInvoiceFormSchema>;

const AP_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "open", label: "Open" },
  { value: "approved", label: "Approved" },
  { value: "on_hold", label: "On Hold" },
  { value: "paid", label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "void", label: "Void" },
  { value: "disputed", label: "Disputed" },
];

export default function ApInvoiceDetailPage() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [editOpen, setEditOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: invoice, isLoading, error } = useQuery<any>({
    queryKey: ["/api/ap-invoices", params.id],
  });

  const { data: payments = [] } = useQuery<any[]>({
    queryKey: ["/api/ap-invoices", params.id, "payments"],
    enabled: !!params.id,
  });

  const form = useForm<ApInvoiceFormData>({
    resolver: zodResolver(apInvoiceFormSchema),
    defaultValues: {
      status: "draft",
      paymentTerms: "",
      notes: "",
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: ApInvoiceFormData) => {
      return apiRequest("PATCH", `/api/ap-invoices/${params.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ap-invoices", params.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/ap-invoices"] });
      toast({ title: "Vendor Invoice updated" });
      setEditOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update invoice", variant: "destructive" });
    },
  });

  const handleEditOpen = () => {
    if (invoice) {
      form.reset({
        status: invoice.status || "draft",
        dueDate: invoice.dueDate || "",
        paymentTerms: invoice.paymentTerms || "",
        notes: invoice.notes || "",
      });
    }
    setEditOpen(true);
  };

  const onSubmit = (data: ApInvoiceFormData) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Vendor Invoice not found</p>
        <Button variant="outline" onClick={() => setLocation("/ap-invoices")}>
          Back to AP Invoices
        </Button>
      </div>
    );
  }

  const lines = invoice.lines || [];

  const statusDotColor = (() => {
    switch (invoice.status) {
      case "open": return "bg-blue-500";
      case "approved": return "bg-green-500";
      case "paid": return "bg-green-600";
      case "on_hold": return "bg-yellow-500";
      case "void": return "bg-red-500";
      case "disputed": return "bg-orange-500";
      default: return "bg-gray-400";
    }
  })();

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLocation("/ap-invoices")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight">
                {invoice.supplierInvoiceNumber}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-2xl font-bold tracking-tight pl-7 flex-wrap">
              {invoice.vendor ? (
                <>
                  <span className="text-blue-600 dark:text-blue-400">
                    {invoice.vendor.name}
                  </span>
                  <span className="text-muted-foreground font-normal">/</span>
                </>
              ) : invoice.parsedVendorName ? (
                <>
                  <span className="italic text-orange-600 dark:text-orange-400" title="Vendor not linked yet — edit this bill to pick one">
                    {invoice.parsedVendorName}
                  </span>
                  <span className="text-muted-foreground font-normal">/</span>
                </>
              ) : null}
              <span className="text-muted-foreground font-normal">Status:</span>
              <span className="capitalize">{invoice.status.replace('_', ' ')}</span>
              {invoice.sourceAttachmentId && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 self-center"
                  title="This bill was created from an AI-uploaded document"
                  data-testid="badge-ai-source"
                >
                  <Sparkles className="h-3 w-3" /> AI Extracted
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleEditOpen}>
              <Edit className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Status</span>
            <div className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full shrink-0", statusDotColor)} />
              <span className="text-sm font-medium capitalize">{invoice.status.replace('_', ' ')}</span>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Invoice Date</span>
              <div className="text-sm font-medium">
                {invoice.invoiceDate ? format(new Date(invoice.invoiceDate), "MMM d, yyyy") : "-"}
              </div>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Due Date</span>
              <div className="text-sm font-medium">
                {invoice.dueDate ? format(new Date(invoice.dueDate), "MMM d, yyyy") : "-"}
              </div>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Total</span>
              <div className="text-sm font-medium">${parseFloat(invoice.totalAmount || "0").toFixed(2)}</div>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Balance</span>
              <div className="text-sm font-medium">${parseFloat(invoice.balance || "0").toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Lines */}
      <div className="border rounded-lg bg-card">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-semibold">Invoice Lines</h2>
            <Badge variant="secondary" className="ml-auto">{lines.length}</Badge>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead className="text-right">Unit Cost</TableHead>
                <TableHead className="text-right">Extended</TableHead>
                <TableHead>Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No invoice lines found
                  </TableCell>
                </TableRow>
              ) : (
                lines.map((line: any) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <div className="font-medium">{line.description}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{line.quantity || "-"}</TableCell>
                    <TableCell>{line.uom || "-"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {line.unitCost ? `$${parseFloat(line.unitCost).toFixed(2)}` : "-"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      ${parseFloat(line.extendedAmount || "0").toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {line.lineType}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Payments */}
      {payments.length > 0 && (
        <div className="border rounded-lg bg-card">
          <div className="p-4 border-b">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold">Payments</h2>
              <Badge variant="secondary" className="ml-auto">{payments.length}</Badge>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payment Date</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment: any) => (
                  <TableRow key={payment.id}>
                    <TableCell>
                      {payment.paymentDate ? format(new Date(payment.paymentDate), "MMM d, yyyy") : "-"}
                    </TableCell>
                    <TableCell className="capitalize">{payment.paymentMethod || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{payment.referenceNumber || "-"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      ${parseFloat(payment.appliedAmount || "0").toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Edit Drawer */}
      <FormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit Vendor Invoice"
        description="Update the vendor invoice details"
        formId="edit-ap-invoice"
        isPending={updateMutation.isPending}
        submitLabel="Save Changes"
        size="md"
        data-testid="drawer-edit-ap-invoice"
      >
        <Form {...form}>
          <form id="edit-ap-invoice" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {AP_STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paymentTerms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Terms</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} placeholder="e.g., NET30" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea variant="flat" {...field} rows={3} placeholder="Add notes..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
